// Sigstore (cosign) trust-policy tab body — extracted from the
// original AdminSigstorePolicies page so it can render inside the
// consolidated `/admin/trust` shell alongside X.509 and OIDC tabs.

import { useState } from 'react';
import { Plus, Trash2, Edit } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useSigstorePolicies,
  createSigstorePolicy,
  updateSigstorePolicy,
  deleteSigstorePolicy,
  type SigstorePolicy,
  type SigstorePolicyInput,
} from '../../api/sigstore';

function emptyDraft(): SigstorePolicyInput {
  return {
    name: '',
    repo_id: null,
    identity_pattern: String.raw`^https://gitlab\.com/acme/.*$`,
    issuer_pattern: String.raw`^https://gitlab\.com$`,
    fulcio_root_ca: '',
    rekor_url: 'https://rekor.sigstore.dev',
    require_rekor: false,
    enabled: true,
  };
}

const TEXTAREA_CLASS =
  'w-full px-3 py-2 rounded-md bg-bg-inset text-fg placeholder:text-fg-subtle border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm font-mono';
const LABEL_CLASS = 'text-sm text-fg-muted block mb-1.5';

export function AdminTrustSigstoreTab() {
  const { data, error, mutate } = useSigstorePolicies();
  const [draft, setDraft] = useState<SigstorePolicyInput>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const policies = data?.policies ?? [];

  function loadIntoForm(p: SigstorePolicy) {
    setDraft({
      name: p.name,
      repo_id: p.repo_id ?? null,
      identity_pattern: p.identity_pattern,
      issuer_pattern: p.issuer_pattern,
      fulcio_root_ca: p.fulcio_root_ca,
      rekor_url: p.rekor_url,
      require_rekor: p.require_rekor,
      enabled: p.enabled,
    });
    setEditingId(p.id);
    setErrorMsg(null);
  }

  function resetForm() {
    setDraft(emptyDraft());
    setEditingId(null);
    setErrorMsg(null);
  }

  async function handleSubmit() {
    setBusy(true);
    setErrorMsg(null);
    try {
      if (editingId) {
        await updateSigstorePolicy(editingId, draft);
      } else {
        await createSigstorePolicy(draft);
      }
      await mutate();
      resetForm();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this Sigstore trust policy? This cannot be undone.')) {
      return;
    }
    try {
      await deleteSigstorePolicy(id);
      await mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleEnabled(p: SigstorePolicy) {
    try {
      await updateSigstorePolicy(p.id, {
        name: p.name,
        repo_id: p.repo_id ?? null,
        identity_pattern: p.identity_pattern,
        issuer_pattern: p.issuer_pattern,
        fulcio_root_ca: p.fulcio_root_ca,
        rekor_url: p.rekor_url,
        require_rekor: p.require_rekor,
        enabled: !p.enabled,
      });
      await mutate();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <>
      {error && (
        <Card className="mb-4 border-danger/40">
          <CardBody>
            <p className="text-sm text-danger">
              Failed to load policies: {String(error)}
            </p>
          </CardBody>
        </Card>
      )}

      <Card className="mb-6">
        <CardHeader title={editingId ? 'Edit policy' : 'New policy'} />
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS} htmlFor="sigstore-name">Name</label>
              <Input
                id="sigstore-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="acme-gitlab-keyless"
              />
            </div>
            <div>
              <label className={LABEL_CLASS} htmlFor="sigstore-repo-id">
                Repo ID (blank = global)
              </label>
              <Input
                id="sigstore-repo-id"
                value={draft.repo_id ?? ''}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    repo_id: e.target.value.trim() === '' ? null : e.target.value,
                  })
                }
                placeholder="leave empty for whole-instance trust"
              />
            </div>
            <div className="md:col-span-2">
              <label className={LABEL_CLASS} htmlFor="sigstore-identity-pattern">
                Identity pattern (Go regex on leaf SAN)
              </label>
              <Input
                id="sigstore-identity-pattern"
                value={draft.identity_pattern}
                onChange={(e) =>
                  setDraft({ ...draft, identity_pattern: e.target.value })
                }
                placeholder="^https://gitlab\.com/acme/.*$"
              />
            </div>
            <div className="md:col-span-2">
              <label className={LABEL_CLASS} htmlFor="sigstore-issuer-pattern">
                Issuer pattern (Go regex on OIDC issuer)
              </label>
              <Input
                id="sigstore-issuer-pattern"
                value={draft.issuer_pattern}
                onChange={(e) =>
                  setDraft({ ...draft, issuer_pattern: e.target.value })
                }
                placeholder="^https://gitlab\.com$"
              />
            </div>
            <div className="md:col-span-2">
              <label className={LABEL_CLASS} htmlFor="sigstore-fulcio-root-ca">
                Fulcio root CA (PEM, leave empty for the embedded
                public-good Fulcio v1 root)
              </label>
              <textarea
                id="sigstore-fulcio-root-ca"
                className={TEXTAREA_CLASS}
                rows={5}
                value={draft.fulcio_root_ca ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, fulcio_root_ca: e.target.value })
                }
                placeholder="-----BEGIN CERTIFICATE-----&#10;... only for private Sigstore deployments&#10;-----END CERTIFICATE-----"
              />
            </div>
            <div>
              <label className={LABEL_CLASS} htmlFor="sigstore-rekor-url">Rekor URL</label>
              <Input
                id="sigstore-rekor-url"
                value={draft.rekor_url ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, rekor_url: e.target.value })
                }
                placeholder="https://rekor.sigstore.dev"
              />
            </div>
            <div className="flex flex-col gap-2 md:pt-7">
              <label className="flex items-center gap-2 text-sm text-fg-muted">
                <input
                  type="checkbox"
                  checked={draft.require_rekor ?? false}
                  onChange={(e) =>
                    setDraft({ ...draft, require_rekor: e.target.checked })
                  }
                />
                <span>Require Rekor inclusion proof</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-fg-muted">
                <input
                  type="checkbox"
                  checked={draft.enabled ?? true}
                  onChange={(e) =>
                    setDraft({ ...draft, enabled: e.target.checked })
                  }
                />
                <span>Enabled</span>
              </label>
            </div>
          </div>
          {errorMsg && (
            <p className="text-sm text-danger mt-3">{errorMsg}</p>
          )}
          <div className="flex gap-2 mt-4">
            <Button variant="primary" onClick={handleSubmit} disabled={busy}>
              <Plus className="h-4 w-4 mr-1" />
              {editingId ? 'Save changes' : 'Create policy'}
            </Button>
            {editingId && (
              <Button variant="secondary" onClick={resetForm}>
                Cancel edit
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Policies" />
        <CardBody>
          {policies.length === 0 ? (
            <p className="text-sm text-fg-muted">
              No Sigstore trust policies configured. Create one above to
              start verifying cosign-signed bundles.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Scope</th>
                    <th className="py-2 pr-4">Identity / Issuer</th>
                    <th className="py-2 pr-4">Rekor</th>
                    <th className="py-2 pr-4">Enabled</th>
                    <th className="py-2 pr-4" />
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p) => (
                    <tr key={p.id} className="border-b border-border/50">
                      <td className="py-2 pr-4 font-medium">{p.name}</td>
                      <td className="py-2 pr-4">
                        {p.repo_id ? (
                          <Badge tone="accent">repo {p.repo_id.slice(0, 8)}</Badge>
                        ) : (
                          <Badge tone="neutral">global</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        <div>{p.identity_pattern}</div>
                        <div className="text-fg-muted">{p.issuer_pattern}</div>
                      </td>
                      <td className="py-2 pr-4 text-xs">
                        {p.require_rekor ? (
                          <Badge tone="warning">required</Badge>
                        ) : (
                          <span className="text-fg-muted">best-effort</span>
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <button
                          className="text-xs underline"
                          onClick={() => toggleEnabled(p)}
                        >
                          {p.enabled ? 'on' : 'off'}
                        </button>
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => loadIntoForm(p)}
                            aria-label="edit"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(p.id)}
                            aria-label="delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
